/**
 * Scroll the focused field clear of the on-screen keyboard.
 *
 * `keyboard-overlap` measures how much of the layout the keyboard covers; this decides
 * what to do about it for a plain form. The two are separate on purpose — the Next Steps
 * reader and the Notes Hub editor both measure the same way and then move something else
 * entirely — so a host opts in by using THIS mixin, which pulls `keyboard-overlap` in
 * with it.
 *
 * -- Why anything is needed at all -------------------------------------------
 *
 * Ionic ships exactly this behaviour, in `input-shims.js` (scroll assist). It never runs
 * on our forms: `startInputShims` registers `ion-input, ion-textarea` and nothing else,
 * and /profile and the six registration screens are built from `CustomTextField`,
 * Vueform's `TextElement`, `vue-tel-input` and `vue3-otp-input` — bare `<input>`s in
 * light DOM, none of which Ionic has ever heard of. So on iOS, where the webview neither
 * resizes nor pans (`Keyboard: { resize: "none" }`), nothing whatsoever moves the field
 * and the bottom of a form sits under the keyboard. Measured 2026-09-08 on an iPhone 17
 * Pro Max simulator: 4/4 cycles, field bottom 734 against a keyboard top of 638, hidden
 * by 96px for the whole cycle.
 *
 * Where it looked like it worked, that was the platform and not us. Android with a
 * resizing window (API 33) shrinks the layout viewport, and the browser's own
 * focus-scroll sometimes takes it from there — sometimes, which is why the same device
 * and the same field failed one sitting and passed the next. Android 16+ is edge-to-edge
 * and PANS: the system slides the webview up itself (pan 0 -> 336), which masks the bug
 * completely. iOS is the only one of the three that fails every time, which makes it the
 * one to develop against.
 *
 * This is NOT an offset problem. `padding-bottom` — which every one of these screens
 * already applies, correctly, from `keyboardInsetCss` — can only add space BELOW the
 * content. Only a scroll lifts anything. Proven on device by overriding that padding to
 * 0px, 48px and 300px: the field stayed exactly where it was in all three.
 *
 * -- Why it waits for the geometry to settle ----------------------------------
 *
 * The obvious implementation — measure at `keyboardDidShow` — is wrong on Android 16+.
 * The pan is still 0 when `didShow` fires and animates in over the following ~900ms, so
 * a measurement taken then reads the full keyboard as covered (952 - 0 - 616 = 336) and
 * scrolls by it; the system then slides the page up underneath, leaving the field a
 * keyboard-height too high. So instead of measuring at a fixed moment, every signal that
 * the geometry moved — both keyboard events, the visual viewport's own `scroll` and
 * `resize` (which is what a pan fires), a window resize — restarts a short quiet timer,
 * and the scroll happens once nothing has moved for KEYBOARD_SETTLE_MS. All of those
 * reach us as changes to `keyboardOverlapPx`, which is why one watcher covers them.
 *
 * By then the three platforms have converged on the same arithmetic:
 *
 *   OVERLAY (iOS)         iH 956, overlap 318 -> visible bottom 638, field 734 -> lift 120
 *   RESIZE  (API 33)      iH 514, overlap 0   -> visible bottom 514, field 626 -> lift 136
 *   PAN     (API 37)      iH 952, overlap 0   -> visible bottom 952, field 732 -> no lift
 *
 * The pan case reaching "no lift" is the point: the settle is what keeps us from adding a
 * second lift on top of the one the OS already performed.
 *
 * -- Why `focusin` and not just the keyboard events ---------------------------
 *
 * Tapping a second field while the keyboard is ALREADY up fires no keyboard event at all,
 * on any platform. `focusin` is the only signal for that hop, and it is also what tells us
 * which element to scroll to.
 *
 * It is listened for on `document` and then scoped to the host's own `ion-content`, which
 * matters more than it looks: Ionic keeps a page mounted after you navigate away from it,
 * and the Capacitor keyboard events are global. A backgrounded /profile therefore hears
 * every keyboard the app raises, and without the scope check it would happily scroll
 * whatever page IS in front — including the Next Steps reader and the Notes Hub, which do
 * their own, different lifting.
 *
 * -- What a host gets ---------------------------------------------------------
 *
 * Everything `keyboard-overlap` exposes, plus:
 *
 *   hasFocusedField   data   whether a field has focus, kept TRUE across the keyboard's
 *                            dismissal animation. The reserved padding has to outlive
 *                            `keyboardActive`, which the mixin below clears at willHide —
 *                            dropping the space while the keyboard is still on screen
 *                            shortens the page under the reader. Cleared at didHide.
 *
 * A host still calls `startKeyboardOverlapTracking()` as it did before; the `focusin`
 * listener installs and removes itself. A host that
 * overrides `onKeyboardOverlapShown` / `onKeyboardOverlapDidHide` replaces this mixin's
 * versions outright (Options API has no super), so don't — if a screen needs both, it
 * wants `keyboard-overlap` directly and its own scrolling.
 */
import keyboardOverlap from './keyboard-overlap';

// How long the geometry has to hold still before the position is believed. Long enough
// to outlast a frame gap during the Android 16+ pan (which fires continuously for
// ~900ms), short enough not to read as lag once the keyboard has finished animating.
export const KEYBOARD_SETTLE_MS = 200;
// Breathing room left between the field and the top of the keyboard, so the field does
// not sit flush against it and its error text has somewhere to appear. Matches the
// clearance the Next Steps reader uses for a focused blank.
const KEYBOARD_CLEARANCE_PX = 24;

// What counts as a field worth scrolling to. Deliberately not `[contenteditable]` bare:
// `contenteditable="false"` matches that and is not editable.
const FIELD_SELECTOR =
  'input, textarea, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]';

export default {
  mixins: [keyboardOverlap],
  data() {
    return {
      // Read by the templates that reserve keyboard-sized padding. See the header.
      hasFocusedField: false,
      // The field to bring into view, and the scrollport it lives in. DOM nodes in `data`
      // are left alone by Vue 3 — reactive() only proxies plain objects, arrays and the
      // Map/Set family — so these are the real elements, not proxies whose native methods
      // would refuse to run.
      keyboardFieldEl: null,
      keyboardHostContentEl: null,
      keyboardHostContentMissed: false,
      keyboardSettleTimer: null,
    };
  },
  watch: {
    // Every geometry signal the mixin below tracks — both keyboard events, the visual
    // viewport's resize and scroll, a window resize — lands here as a new overlap. One
    // watcher is therefore the whole "something moved" story; see the header.
    keyboardOverlapPx() {
      this.scheduleKeyboardFieldScroll();
    },
  },
  mounted() {
    // Capture phase: these fields live inside child components, and a focus handler
    // there is free to stop the bubble.
    document.addEventListener('focusin', this.onKeyboardFieldFocusIn, true);
  },
  unmounted() {
    document.removeEventListener('focusin', this.onKeyboardFieldFocusIn, true);
    clearTimeout(this.keyboardSettleTimer);
  },
  methods: {
    onKeyboardOverlapShown() {
      this.hasFocusedField = true;
      this.scheduleKeyboardFieldScroll();
    },
    onKeyboardOverlapDidHide() {
      this.hasFocusedField = false;
      this.keyboardFieldEl = null;
      clearTimeout(this.keyboardSettleTimer);
      this.keyboardSettleTimer = null;
    },

    // The `ion-content` this host scrolls in. Resolved on first use rather than at mount:
    // the host is sometimes INSIDE the content (the registration steps, which render into
    // their layout's slot) and sometimes owns it a level down (/profile, whose root is the
    // `ion-page` that MasterLayout renders), and a page still laying out at mount would
    // answer neither. Cached once found, and resolved again if the cached element has
    // since left the document: a layout that re-keys its `ion-content` hands the host a
    // fresh scroller, and a detached one answers no to `contains` for every field, which
    // stops the scroll silently. (MyGivingLayout did exactly that, keyed on the route,
    // until the key was removed; the guard stays because it is cheap and general.)
    keyboardHostContent() {
      const cached = this.keyboardHostContentEl;
      if (cached) {
        if (cached.isConnected) return cached;
        this.keyboardHostContentEl = null;
      }
      if (this.keyboardHostContentMissed) return null;
      const root = this.$el;
      // A fragment-rooted host's $el is an anchor text node; its parent is in the content.
      const el = root?.nodeType === 1 ? root : root?.parentElement;
      const content = el?.closest?.('ion-content') || el?.querySelector?.('ion-content') || null;
      // The miss is cached as deliberately as the hit. This answers on every focus event
      // anywhere in the app, and a host with no ion-content would otherwise re-run a
      // querySelector over its entire subtree — for /profile, the whole form — each time.
      if (content) this.keyboardHostContentEl = content;
      else this.keyboardHostContentMissed = true;
      return content;
    },

    onKeyboardFieldFocusIn(event) {
      const el = event?.target;
      if (!el?.matches?.(FIELD_SELECTOR)) return;
      // Someone else's page — see the header.
      if (!this.keyboardHostContent()?.contains(el)) return;
      this.keyboardFieldEl = el;
      this.hasFocusedField = true;
      // A field-to-field hop fires no keyboard event at all, so this is the only
      // trigger it gets.
      this.scheduleKeyboardFieldScroll();
    },

    // Restart the quiet timer. Every geometry signal lands here, so the scroll happens
    // once nothing has moved for KEYBOARD_SETTLE_MS — see the header.
    //
    // The single `keyboardActive` gate for the whole mixin: a focus with no keyboard up
    // has nothing to measure against, and `keyboardWillShow` will schedule it a moment
    // later anyway.
    scheduleKeyboardFieldScroll() {
      if (!this.keyboardActive) return;
      clearTimeout(this.keyboardSettleTimer);
      this.keyboardSettleTimer = setTimeout(() => {
        this.keyboardSettleTimer = null;
        this.scrollFocusedFieldClearOfKeyboard();
      }, KEYBOARD_SETTLE_MS);
    },

    // Move the focused field into the strip of screen the keyboard leaves visible.
    //
    // Worked in VIEWPORT coordinates rather than against the scrollport's own height:
    // the overlap is measured from the bottom of the WINDOW, so on a page whose
    // `ion-content` stops short of it (a footer, a home indicator) subtracting the whole
    // overlap from the scrollport would over-lift. `Math.min` of the two edges is the
    // one that is actually visible.
    //
    // `scrollIntoView({ block: 'center' })` is not a substitute: where the keyboard
    // overlays the webview the scrollport is still full-screen height, so its centre is
    // measured against a viewport whose bottom third the keyboard is sitting on.
    async scrollFocusedFieldClearOfKeyboard() {
      if (!this.keyboardActive) return;
      const el = this.keyboardFieldEl;
      // Not `isConnected`: Ionic keeps a backgrounded page mounted and hides it with
      // `display: none`, so a field left over from that page is still connected while
      // every rect reads 0 — which lands a -24px delta on a hidden scroller. Focus is
      // what this actually depends on, and it covers disconnection too.
      if (!el || document.activeElement !== el) return;
      const content = this.keyboardHostContent();
      if (typeof content?.getScrollElement !== 'function') return;

      let scroller;
      try {
        scroller = await content.getScrollElement();
      } catch (e) {
        return;
      }
      // One microtask, but the field can still lose focus across it.
      if (!scroller || document.activeElement !== el) return;

      const scrollerRect = scroller.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const keyboardTop = (window.innerHeight || 0) - this.keyboardOverlapPx;
      const visibleBottom = Math.min(scrollerRect.bottom, keyboardTop) - KEYBOARD_CLEARANCE_PX;
      const visibleTop = scrollerRect.top + KEYBOARD_CLEARANCE_PX;

      let delta = 0;
      if (rect.bottom > visibleBottom) {
        delta = rect.bottom - visibleBottom;
        // Never push the top of the field out of sight to reveal its bottom — a field
        // taller than the visible strip is better anchored at its top.
        delta = Math.min(delta, rect.top - visibleTop);
      } else if (rect.top < visibleTop) {
        // Already clear of the keyboard but tucked under the header — nudge it down.
        delta = rect.top - visibleTop;
      }
      if (Math.abs(delta) < 1) return;
      scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
    },
  },
};
